import { useState } from 'react';

import {
  add,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
  startOfYear
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import { dateRangeAtom, selectedChipAtom } from '../../atoms';

import { Box, Typography } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';

import { Select } from 'components/shared';

import { ChipSelection, SelectedChip } from '../../enum';
import Chips from './Chips';
import MonthOrWeekRange from './MonthOrWeekRange';

export type PeriodType = 'month' | 'week';

export default function DateSelector() {
  const { t } = useTranslation('common');
  const options = [
    { value: 'month', label: t('month') },
    { value: 'week', label: t('week') }
  ];
  const [selectedPeriodType, setSelectedPeriodType] = useState(
    options[0].value
  );
  const [dateRange, setDateRange] = useRecoilState(dateRangeAtom);
  const [selectedChip, setSelectedChip] = useRecoilState(selectedChipAtom);

  const [showDateRange, setShowDateRange] = useState(false);
  const isSetToMonth = selectedPeriodType === 'month';

  const handleChipClick = (chip: SelectedChip) => {
    setSelectedChip(chip);
    setShowDateRange(false);

    const today = new Date();
    const lastMonth = add(today, { months: -1 });
    const nextMonth = add(today, { months: 1 });
    const lastWeek = add(today, { weeks: -1 });
    const nextWeek = add(today, { weeks: 1 });

    switch (chip) {
      case ChipSelection.UNTIL_LAST_MONTH:
        setDateRange([startOfYear(lastMonth), endOfMonth(lastMonth)]);
        break;
      case ChipSelection.LAST_MONTH:
        setDateRange([startOfMonth(lastMonth), endOfMonth(lastMonth)]);
        break;
      case ChipSelection.THIS_MONTH:
        setDateRange([startOfMonth(today), endOfMonth(today)]);
        break;
      case ChipSelection.NEXT_MONTH:
        setDateRange([startOfMonth(nextMonth), endOfMonth(nextMonth)]);
        break;
      case ChipSelection.UNTIL_LAST_WEEK:
        setDateRange([startOfYear(lastWeek), endOfWeek(lastWeek)]);
        break;
      case ChipSelection.LAST_WEEK:
        setDateRange([startOfWeek(lastWeek), endOfWeek(lastWeek)]);
        break;
      case ChipSelection.THIS_WEEK:
        setDateRange([startOfWeek(today), endOfWeek(today)]);
        break;
      case ChipSelection.NEXT_WEEK:
        setDateRange([startOfWeek(nextWeek), endOfWeek(nextWeek)]);
        break;
      case ChipSelection.CUSTOM:
        setDateRange([null, null]);
        setShowDateRange(true);
        break;
      default:
        return;
    }
  };

  const handleStartDateChange = (d: Date | null) => {
    const newDate: DateRange<Date | null> = [...dateRange];
    if (isSetToMonth) {
      newDate[0] = startOfMonth(d as Date);
    } else {
      newDate[0] = startOfWeek(d as Date, { weekStartsOn: 1 });
    }
    setDateRange(newDate);
  };

  const handleEndDateChange = (d: Date | null) => {
    const newDate: DateRange<Date | null> = [...dateRange];

    if (isSetToMonth) {
      newDate[1] = endOfMonth(d as Date);
    } else {
      newDate[1] = endOfWeek(d as Date, { weekStartsOn: 1 });
    }
    setDateRange(newDate);
  };

  const handleSelect = (val: string) => {
    setSelectedChip('');
    setDateRange([null, null]);
    setShowDateRange(false);

    setSelectedPeriodType(val);
  };

  return (
    <Box pb={4}>
      <Box display="flex" alignItems="end" justifyContent="space-between">
        <Box display="flex" alignItems="end" height="48px" gap={4}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              {t('adminPortal.realization.selectPeriod')}:
            </Typography>
            <Chips
              isSetToMonth={isSetToMonth}
              selectedChip={selectedChip}
              onChipClick={handleChipClick}
            />
          </Box>
          {showDateRange && (
            <MonthOrWeekRange
              isSetToMonth={isSetToMonth}
              dateRange={dateRange}
              onChangeStartDate={handleStartDateChange}
              onChangeEndDate={handleEndDateChange}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center">
          <h4 style={{ marginRight: '16px', flexShrink: 0 }}>
            {t('periodType')}:{' '}
          </h4>
          <Select
            options={options}
            valueProp={selectedPeriodType}
            onSelected={handleSelect}
          />
        </Box>
      </Box>
    </Box>
  );
}
